import { AuthorizationDeniedError } from "./errors.js";
export const capabilityRoles = Object.freeze({
    "staff.provision": Object.freeze(["administrator"]),
    "staff.manage_roles": Object.freeze(["administrator"]),
    "staff.manage_status": Object.freeze(["administrator"]),
    "assignment.manage": Object.freeze(["supervisor"]),
    "customer.read_all": Object.freeze(["supervisor"]),
    "customer.read_assigned": Object.freeze(["concierge"]),
    "handoff.request_for_customer": Object.freeze(["concierge", "supervisor"]),
    "handoff.accept": Object.freeze(["concierge", "supervisor"]),
    "handoff.release": Object.freeze(["concierge", "supervisor"]),
    "handoff.reassign": Object.freeze(["supervisor"]),
    "handoff.cancel": Object.freeze(["supervisor"]),
    "handoff.read_internal": Object.freeze(["concierge", "supervisor"]),
    "request.create_for_customer": Object.freeze(["concierge", "supervisor"]),
    "request.assign": Object.freeze(["supervisor"]),
    "request.work": Object.freeze(["concierge", "supervisor"]),
    "request.reassign_owner": Object.freeze(["supervisor"]),
    "request.internal_notes": Object.freeze(["concierge", "supervisor"]),
    "request.read_internal": Object.freeze(["concierge", "supervisor"]),
    "trip.read": Object.freeze(["concierge", "supervisor"]),
    "trip.manage": Object.freeze(["concierge", "supervisor"]),
    "memory.read": Object.freeze(["concierge", "supervisor"]),
    "memory.manage": Object.freeze(["concierge", "supervisor"]),
    "catalog.edit": Object.freeze(["catalog_editor"]),
    "catalog.publish": Object.freeze(["catalog_editor"]),
    // Owner decision O1. Price edit and price approval can be done by the same role; the audit
    // trail (actor, reason, correlation id) and the DB rule that any price edit clears the mark
    // are the controls, not a second approver.
    "catalog.authorize_price": Object.freeze(["catalog_editor", "supervisor"]),
    "catalog.read_internal": Object.freeze(["catalog_editor", "concierge", "supervisor"]),
    "catalog.add_internal_note": Object.freeze([
        "catalog_editor",
        "concierge",
        "supervisor",
    ]),
    "catalog.import": Object.freeze(["catalog_editor"]),
    // Assignment-scoped (an effective assignment or the supervisor role).
    "payment.read": Object.freeze(["concierge", "supervisor"]),
    "payment.create_for_customer": Object.freeze(["concierge", "supervisor"]),
    // The request owner or a supervisor (a quote is recorded and approved by one act).
    "payment.record_quote": Object.freeze(["concierge", "supervisor"]),
    "payment.issue_quote_request": Object.freeze(["concierge", "supervisor"]),
    "payment.cancel": Object.freeze(["concierge", "supervisor"]),
    // Privileged and not assignment-scoped: an administrator refunds by payment request id and holds
    // no customer-read capability (ruling Q4).
    "payment.refund": Object.freeze(["supervisor", "administrator"]),
    "payment.resolve_exception": Object.freeze(["supervisor"]),
    // CHT-761. Not assignment-scoped: the list holds ids, codes and counts, never customer content.
    "operations.jobs.read": Object.freeze(["supervisor", "administrator"]),
    "operations.jobs.manage": Object.freeze(["administrator"]),
    // CHT-772. Not assignment-scoped: the view holds no message text, no note and no metadata, only who did what to which record and why.
    "audit.read": Object.freeze(["supervisor", "administrator"]),
    // Assignment-scoped for a concierge (an effective assignment to the customer); the supervisor reconciles any.
    "payment.reconcile": Object.freeze(["concierge", "supervisor"]),
    // The Payments console shows ids, status, amounts, flags and the provider trace: no message text and no contact detail.
    "payment.read_console": Object.freeze(["concierge", "supervisor", "administrator"]),
    // CHT-771. Destructive and sensitive: supervisor and administrator only. A concierge and a catalog editor hold none of them.
    "privacy.read": Object.freeze(["supervisor", "administrator"]),
    "privacy.request": Object.freeze(["supervisor", "administrator"]),
    "privacy.approve": Object.freeze(["supervisor", "administrator"]),
    "privacy.download_export": Object.freeze(["supervisor", "administrator"]),
    // CHT-2027. The role part of the rule. The customer part (the assignment of a concierge) is `decideCustomerAnalyticsAccess`; a catalog
    // editor holds none.
    "analytics.read_customer": Object.freeze(["concierge", "supervisor", "administrator"]),
});
/**
 * Whether one of the roles holds the capability: the one role rule. `authorizeStaff` (the server) and the staff console (to decide what it
 * offers) both read it. It looks at roles only: it does not check status, MFA or any relationship, so it never authorizes a request.
 */
export function rolesHoldCapability(roles, capability) {
    const held = new Set(roles);
    return capabilityRoles[capability].some((role) => held.has(role));
}
/**
 * Decides from the authority just read from Postgres. Checks run in this order, so the code names
 * the first thing that is wrong: a missing or DEACTIVATED user is `staff_inactive`; in production
 * a session without aal2 is `mfa_required`; a user without a granting role is `role_missing`.
 */
export function authorizeStaff(authority, capability, environment) {
    if (authority === null || authority.status !== "ACTIVE") {
        return { allowed: false, code: "staff_inactive" };
    }
    if (environment === "production" && !authority.mfaSatisfied) {
        return { allowed: false, code: "mfa_required" };
    }
    if (!rolesHoldCapability(authority.roles, capability)) {
        return { allowed: false, code: "role_missing" };
    }
    return { allowed: true };
}
/** An administrator may not change their own grants or status. */
export function assertNotSelf(actorId, targetId) {
    if (actorId === targetId)
        throw new AuthorizationDeniedError("self_modification");
}
/** The relationship of a staff actor who holds none: the value for a path that has no customer or resource to relate to. */
export const NO_STAFF_RELATIONSHIP = Object.freeze({
    hasEffectiveAssignment: false,
    isTarget: false,
    isOwner: false,
});
/**
 * The relationship that the transition machines read, from the facts. An assignment counts only while the actor holds an unrevoked
 * `concierge` grant, as in the row-level security read rule: a staff member who lost the grant reaches nothing through an assignment that
 * has not been ended yet.
 */
export function staffRelationshipOf(authority, facts) {
    return {
        hasEffectiveAssignment: facts.assignment !== null && authority.roles.has("concierge"),
        isTarget: facts.isTarget,
        isOwner: facts.isOwner,
    };
}
/** Whether the relationship meets the policy. The one predicate behind every staff access gate. */
export function staffRelationshipAllows(authority, relationship, policy) {
    const supervisor = authority.roles.has("supervisor");
    switch (policy) {
        case "CUSTOMER_DATA":
            return relationship.hasEffectiveAssignment || supervisor;
        case "RESOURCE":
            return (relationship.isOwner ||
                relationship.isTarget ||
                relationship.hasEffectiveAssignment ||
                supervisor);
        case "WORK":
            return relationship.isOwner || relationship.isTarget || supervisor;
    }
}
/** The machine form of a refusal: `relationship_missing`, never a hint of the state of the resource. */
export function assertStaffRelationship(authority, relationship, policy) {
    if (!staffRelationshipAllows(authority, relationship, policy)) {
        throw new AuthorizationDeniedError("relationship_missing");
    }
}
/**
 * Decides, and says why. Pure: the caller read the facts after its locks, with the fresh database clock (`readStaffAccessFacts`).
 * The reason names the first thing that grants, in the order the screens show it: for `CUSTOMER_DATA` the assignment, then the supervisor
 * role; for `RESOURCE` the owner, the target, the supervisor role, then the assignment; for `WORK` the owner, the target, then the
 * supervisor role.
 */
export function decideStaffAccess(authority, facts, policy) {
    const relationship = staffRelationshipOf(authority, facts);
    if (!staffRelationshipAllows(authority, relationship, policy)) {
        return { allowed: false, code: "relationship_missing" };
    }
    const assignment = relationship.hasEffectiveAssignment ? facts.assignment : null;
    const supervisor = authority.roles.has("supervisor");
    const reason = policy === "CUSTOMER_DATA"
        ? (assignment ?? (supervisor ? "SUPERVISOR" : null))
        : relationship.isOwner
            ? "OWNER"
            : relationship.isTarget
                ? "TARGET"
                : supervisor
                    ? "SUPERVISOR"
                    : policy === "RESOURCE"
                        ? assignment
                        : null;
    // Unreachable: `staffRelationshipAllows` granted, so one of the facts above holds.
    if (reason === null)
        return { allowed: false, code: "relationship_missing" };
    return { allowed: true, reason };
}
export function isSupervisor(authority) {
    return authority.roles.has("supervisor");
}
/** Whether the staff user reads every customer's payments in the console (a supervisor or an administrator), not only assigned ones. */
export function readsAllPayments(authority) {
    return authority.roles.has("supervisor") || authority.roles.has("administrator");
}
/** The id an audit row or history row records for the actor. */
export function transitionActorId(actor) {
    switch (actor.kind) {
        case "customer":
            return actor.customerId;
        case "staff":
            return actor.authority.staffUserId;
        case "system":
            return actor.actorId;
    }
}
/**
 * The role-level gate of a transition that only staff may drive: a customer or a system actor is
 * `role_missing`, and a staff actor passes `authorizeStaff` for `capability` (inactive, MFA and
 * role checks, in that order). The relationship rule of the transition comes after this.
 */
export function assertStaffCapability(actor, capability, environment) {
    if (actor.kind !== "staff")
        throw new AuthorizationDeniedError("role_missing");
    const decision = authorizeStaff(actor.authority, capability, environment);
    if (!decision.allowed)
        throw new AuthorizationDeniedError(decision.code);
    return actor;
}
